"use client";

import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

import type { AgentContext } from "@/lib/api/recruiter";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ContextViewerProps {
  context: AgentContext | null;
}

// ---------------------------------------------------------------------------
// Pill styles (reused from Phase 13 shortlist-item-row.tsx)
// ---------------------------------------------------------------------------

const pillBase: React.CSSProperties = {
  borderRadius: "12px",
  padding: "0.12rem 0.45rem",
  fontSize: "0.73rem",
  display: "inline-block",
  whiteSpace: "nowrap",
};

const pillDefault: React.CSSProperties = {
  ...pillBase,
  color: "color-mix(in srgb, var(--accent-blue) 80%, #fff 20%)",
  border: "1px solid color-mix(in srgb, var(--accent-blue) 44%, var(--border) 56%)",
  background: "color-mix(in srgb, var(--accent-blue) 18%, var(--surface-elevated) 82%)",
};

const pillMustHave: React.CSSProperties = {
  ...pillBase,
  color: "color-mix(in srgb, var(--accent-blue) 80%, #fff 20%)",
  border: "1px solid color-mix(in srgb, var(--accent-blue) 44%, var(--border) 56%)",
  background: "color-mix(in srgb, var(--accent-blue) 28%, var(--surface-elevated) 72%)",
};

const pillNiceToHave: React.CSSProperties = {
  ...pillBase,
  color: "var(--text-secondary)",
  border: "1px solid var(--border)",
  background: "var(--surface-elevated)",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function countRequirements(ctx: AgentContext | null): number {
  if (!ctx) return 0;
  let count = 0;
  if (ctx.role_type) count++;
  if (ctx.seniority) count++;
  if (ctx.tech_stack?.length) count += ctx.tech_stack.length;
  if (ctx.location_preference) count++;
  if (ctx.must_haves?.length) count += ctx.must_haves.length;
  if (ctx.nice_to_haves?.length) count += ctx.nice_to_haves.length;
  return count;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ContextViewer({ context }: ContextViewerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const reqCount = countRequirements(context);
  const ChevronIcon = isExpanded ? ChevronUp : ChevronDown;

  return (
    <div style={styles.wrapper}>
      {/* Header row */}
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        style={styles.header}
      >
        <span style={styles.headerLabel}>Requirements</span>
        <span style={styles.headerRight}>
          <span style={styles.countText}>
            {reqCount > 0
              ? `${reqCount} requirement${reqCount === 1 ? "" : "s"} extracted`
              : "No requirements yet"}
          </span>
          <ChevronIcon size={16} style={{ color: "var(--text-tertiary)" }} />
        </span>
      </button>

      {/* Expanded content */}
      {isExpanded && context && reqCount > 0 && (
        <div style={styles.body}>
          {/* Role type */}
          {context.role_type && (
            <PillGroup label="Role">
              <span style={pillDefault}>{context.role_type}</span>
            </PillGroup>
          )}

          {/* Seniority */}
          {context.seniority && (
            <PillGroup label="Seniority">
              <span style={pillDefault}>{context.seniority}</span>
            </PillGroup>
          )}

          {/* Tech stack */}
          {context.tech_stack && context.tech_stack.length > 0 && (
            <PillGroup label="Tech Stack">
              {context.tech_stack.map((tech) => (
                <span key={tech} style={pillDefault}>
                  {tech}
                </span>
              ))}
            </PillGroup>
          )}

          {/* Location */}
          {context.location_preference && (
            <PillGroup label="Location">
              <span style={pillDefault}>{context.location_preference}</span>
            </PillGroup>
          )}

          {/* Must-haves */}
          {context.must_haves && context.must_haves.length > 0 && (
            <PillGroup label="Must-Haves">
              {context.must_haves.map((item) => (
                <span key={item} style={pillMustHave}>
                  {item}
                </span>
              ))}
            </PillGroup>
          )}

          {/* Nice-to-haves */}
          {context.nice_to_haves && context.nice_to_haves.length > 0 && (
            <PillGroup label="Nice-to-Haves">
              {context.nice_to_haves.map((item) => (
                <span key={item} style={pillNiceToHave}>
                  {item}
                </span>
              ))}
            </PillGroup>
          )}

          {/* Company context */}
          {context.company_context && (
            <div style={styles.companyContext}>
              <span style={styles.sectionLabel}>CONTEXT</span>
              <p style={styles.companyText}>{context.company_context}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PillGroup helper
// ---------------------------------------------------------------------------

function PillGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={styles.pillGroup}>
      <span style={styles.sectionLabel}>{label.toUpperCase()}</span>
      <div style={styles.pillRow}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    borderBottom: "1px solid var(--border)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "0.6rem 1rem",
    background: "transparent",
    border: "none",
    cursor: "pointer",
  },
  headerLabel: {
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "var(--text-primary)",
    letterSpacing: "0.02em",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
  },
  countText: {
    fontSize: "0.75rem",
    color: "var(--text-tertiary)",
  },
  body: {
    padding: "0 1rem 0.75rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  pillGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  },
  sectionLabel: {
    fontSize: "0.65rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: "var(--text-tertiary)",
  },
  pillRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.3rem",
  },
  companyContext: {
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
  },
  companyText: {
    fontSize: "0.78rem",
    color: "var(--text-secondary)",
    lineHeight: 1.4,
    margin: 0,
  },
};
